import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

import { FieldLabel } from './FieldLabel';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string | undefined;
  /**
   * Texto do icone de ajuda (i). Sem ele, o campo usa o dicionario de ajuda pela
   * tela e pelo rotulo; false desliga a ajuda.
   */
  help?: string | false | undefined;
  error?: string | undefined;
  leading?: ReactNode | undefined;
  /**
   * Esconde o rotulo visualmente, sem tira-lo do leitor de tela.
   *
   * Existe para a linha de tabela em que o cabecalho ja diz o que o campo e, e
   * repetir o rotulo em cada linha seria ruido. Remover o rotulo em vez de
   * esconde-lo deixaria o campo sem nome para quem nao ve a tabela.
   */
  labelHidden?: boolean | undefined;
}

/**
 * Campo de formulario com rotulo, apoio e erro sempre associados ao controle.
 *
 * A associacao por identificador nao e detalhe cosmetico: sem ela, o leitor de
 * tela anuncia um campo sem nome e nao le a mensagem de erro.
 */
export function Field({
  label,
  hint,
  help,
  error,
  leading,
  labelHidden,
  className,
  ...rest
}: FieldProps) {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel
        htmlFor={inputId}
        label={label}
        className={labelHidden ? 'sr-only' : 'text-xs font-semibold text-ink-700'}
        help={labelHidden ? false : help}
      />

      <div className="relative">
        {leading ? (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-400">
            {leading}
          </span>
        ) : null}

        <input
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={cn(
            'h-10 w-full rounded-control border bg-surface px-3 text-sm text-ink-900 shadow-(--shadow-xs)',
            'placeholder:text-ink-400',
            'transition-[border-color,box-shadow] duration-(--duration-fast)',
            'focus:outline-none focus:ring-4',
            leading ? 'pl-9' : null,
            error
              ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/15'
              : 'border-line-strong hover:border-ink-400 focus:border-brand-500 focus:ring-brand-500/15',
            className,
          )}
          {...rest}
        />
      </div>

      {hint && !error ? (
        <p id={hintId} className="text-2xs text-ink-500">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="text-2xs font-medium text-danger-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
